import groupBy from 'lodash/groupBy';
import uniqBy from 'lodash/uniqBy';

import type { Capacity } from '~/consts/capacities';

import type { AppliedFlashRoomRate } from '../types';

// Both combinations, not just one: capacity gates which occupancy searches can find a room rate at
// all, and 1A-only would leave it invisible to the most common leisure booking (a couple).
export const DEFAULT_CAPACITY_COMBINATIONS: Array<Capacity> = [
  { adults: 1, children: 0, infants: 0, teenagers: 0 },
  { adults: 2, children: 0, infants: 0, teenagers: 0 },
];

export type CapacityRoomRate = Pick<AppliedFlashRoomRate, 'ratePlanName' | 'roomName' | 'roomRateId' | 'roomTypeId'>;

export type CapacityRoomTypeGroup = {
  ratePlanNames: Array<string>;
  roomName: string;
  roomRates: Array<CapacityRoomRate>;
  roomTypeId: string;
};

export function getDistinctAppliedRoomRates(
  appliedFlashRoomRates: Array<AppliedFlashRoomRate>,
): Array<CapacityRoomRate> {
  return uniqBy(appliedFlashRoomRates, 'roomRateId');
}

// Capacity (max occupancy) is a room-type-level fact in practice, even though the API writes it
// per room rate -- group so "default to 1A/2A" can be applied once across every rate plan a room type sells.
export function groupRoomRatesByRoomType(roomRates: Array<CapacityRoomRate>): Array<CapacityRoomTypeGroup> {
  const roomRatesByRoomTypeId = groupBy(roomRates, 'roomTypeId');

  return Object.values(roomRatesByRoomTypeId).map((roomRatesForType) => ({
    ratePlanNames: uniqBy(
      roomRatesForType.filter((roomRate) => roomRate.ratePlanName),
      'ratePlanName',
    ).map((roomRate) => roomRate.ratePlanName),
    roomName: roomRatesForType[0].roomName,
    roomRates: roomRatesForType,
    roomTypeId: roomRatesForType[0].roomTypeId,
  }));
}

export type CapacityGroupStatus = 'applied' | 'failed' | 'not-set' | 'partial';

// A room rate already in appliedRoomRateIds has real capacity data, even if a later redundant
// re-apply attempt failed on it (e.g. a duplicate-key rejection) -- applied always wins per room rate.
export function getGroupStatus(
  roomRateIds: Array<string>,
  appliedRoomRateIds: Array<string>,
  failedRoomRateIds: Array<string>,
): CapacityGroupStatus {
  const unappliedRoomRateIds = roomRateIds.filter((id) => !appliedRoomRateIds.includes(id));

  if (unappliedRoomRateIds.some((id) => failedRoomRateIds.includes(id))) {
    return 'failed';
  }

  if (unappliedRoomRateIds.length === 0) {
    return 'applied';
  }

  if (unappliedRoomRateIds.length < roomRateIds.length) {
    return 'partial';
  }

  return 'not-set';
}

export function getApplyAllButtonLabel(isApplying: boolean, unappliedCount: number, totalCount: number) {
  if (isApplying) {
    return 'Applying...';
  }

  return unappliedCount === totalCount
    ? 'Default all room types to 1A / 2A'
    : 'Default remaining room types to 1A / 2A';
}
